import React from 'react';
import { Alert as MuiAlert, AlertProps, alertClasses, AlertTitle, styled, ButtonBase, Box } from '@mui/material';
import { AlertIcon } from './AlertIcons';
// Required for webpack/ts-loader to include the MUI theme augmentation in the bundle.
// Without this import, theme.custom.colours is not recognized during builds.
import '../../theme';

export type ToastAlertProps = {
  severity: AlertProps['severity'];
  onClose: AlertProps['onClose'];
  title: AlertProps['title'];
  subtitle?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
};

const Alert = styled(MuiAlert, {
  shouldForwardProp: (prop) => prop !== 'hasSubtitle',
})<{ hasSubtitle: boolean }>(({ theme, hasSubtitle }) => ({
  [`&.${alertClasses.root}`]: {
    borderRadius: '12px',
    border: `1px solid ${theme.custom.colours?.grey300}`,
    padding: hasSubtitle ? '9px 16px 16px 16px' : '9px 16px',
    boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
    alignItems: hasSubtitle ? 'flex-start' : 'center',
    /**
     * By default we want the icon & action to inherit the color property of the parent
     * as decalred below.
     */
    [`& .${alertClasses.icon}`]: {
      color: 'inherit',
      marginRight: '8px',
      padding: 0,
    },
    [`& .${alertClasses.message}`]: {
      /**
       * The icon has wappers that take up space so we need to add padding to the title when there is a subtitle
       * to make it align with the icon
       */
      paddingTop: hasSubtitle ? '6px' : 0,
      paddingBottom: 0,
      '& .MuiAlertTitle-root': {
        margin: 0,
        fontSize: '14px',
        fontWeight: hasSubtitle ? 600 : 400,
        lineHeight: '20px',
        color: hasSubtitle ? theme.custom.colours?.grey700 : 'inherit',
      },
    },
    [`& .${alertClasses.action}`]: {
      paddingTop: hasSubtitle ? '1px' : 0,
      color: 'inherit',
      alignItems: 'center',
    },
  },
  [`&.${alertClasses.colorInfo}`]: {
    backgroundColor: theme.custom.colours?.grey50,
    color: theme.custom.colours?.grey600,
    border: `1px solid ${theme.custom.colours?.grey300}`,
  },
  [`&.${alertClasses.colorSuccess}`]: {
    backgroundColor: theme.custom.colours?.success50,
    color: theme.custom.colours?.grey600,
    border: `1px solid ${theme.custom.colours?.success600}`,
  },
  [`&.${alertClasses.colorWarning}`]: {
    backgroundColor: theme.custom.colours?.warning50,
    color: theme.custom.colours?.grey600,
    border: `1px solid ${theme.custom.colours?.warning600}`,
  },
  [`&.${alertClasses.colorError}`]: {
    backgroundColor: theme.custom.colours?.error50,
    color: theme.custom.colours?.grey600,
    border: `1px solid ${theme.custom.colours?.error600}`,
  },
}));

const StyledButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: '20px',
}));

const ToastAlertInner = (
  { severity, onClose, title, subtitle, action }: ToastAlertProps,
  ref: React.Ref<HTMLDivElement>,
) => {
  return (
    <Alert
      ref={ref}
      severity={severity}
      onClose={onClose}
      icon={<AlertIcon severity={severity} />}
      hasSubtitle={!!subtitle}
      data-testid={`${severity}-snackbar`}
      title={title}
    >
      {title && <AlertTitle>{title}</AlertTitle>}
      {subtitle}
      {action && (
        <Box marginTop={'10px'}>
          <StyledButton onClick={action.onClick}>{action.label}</StyledButton>
        </Box>
      )}
    </Alert>
  );
};

// Snackbar passes a ref to the Alert component
export const ToastAlert = React.forwardRef(ToastAlertInner);
